import role23MfrsSchema from '../../../../assets/forms/role-740000-mfrs.form.json';
import role23MpersSchema from '../../../../assets/forms/role-740000-mpers.form.json';
import { pickSchema, MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';
import { isEmptyCell } from '../validation-field-key.util';

/**
 * Shared business rules for Role 23 (Notes - Issued Capital).
 *
 * Role 23 is the second lifted sheet whose schema is **accounting-standard
 * dependent** (after role 20). The same logical sheet ships as two sibling
 * form JSONs — role-740000-mfrs.form.json and role-740000-mpers.form.json —
 * that differ in qname prefix (ssmt-mfrs:* vs ssmt-mpers:*) and a couple of
 * control descriptions. The component picks between them at mount time based
 * on the filing's accounting standard; the pure validator has to make the
 * same selection from SheetValidationContext.accountingStandard.
 *
 * Both the editor's role23-740000-notes-issued-capital.component.ts AND
 * role23NotesIssuedCapitalValidator import from this file so the selection
 * literal and the shared table-row primitives live in exactly one place.
 *
 * ---- Table-row repetition ------------------------------------------------
 *
 * Role 23 stores flat sheetData, NOT an array-of-rows. Each schema row with
 * `ui.tableColumn === true` gets expanded at form-build time into 16 flat
 * keys:
 *
 *   {qname}                                         [Consolidated][CurrentYear]    <-- Total column
 *   {qname}                                         [Consolidated][PreviousYear]
 *   {qname}                                         [Separate][CurrentYear]
 *   {qname}                                         [Separate][PreviousYear]
 *   {qname}:OrdinarySharesMember                    [Consolidated][CurrentYear]
 *   {qname}:OrdinarySharesMember                    [Consolidated][PreviousYear]
 *   ... (same for Separate)
 *   {qname}:RedeemablePreferenceSharesMember        [Consolidated][CurrentYear]
 *   ... etc (3 non-Total members x 2 dimensions x 2 periods = 12)
 *   + 4 Total controls (1 base qname x 2 dimensions x 2 periods)
 *   = 16 controls per schema row.
 *
 * So "table-row repetition" here is a virtual expansion at the form layer,
 * not a real array-of-rows shape in sheetData. The pure validator replays
 * the same expansion via buildRowColumnKeys() below, then flags any required
 * column whose value is empty.
 */

/**
 * Literal value of
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects the MPERS schema. Any other value (including undefined, '',
 * or 'Malaysian Financial Reporting Standards') falls back to MFRS, matching
 * the component's `let schemaFile = 'role-740000-mfrs.form.json'` default.
 */
export const ROLE23_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE23_MFRS_SCHEMA_FILE = 'role-740000-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE23_MPERS_SCHEMA_FILE = 'role-740000-mpers.form.json';

/**
 * The Consolidated-dimension gate (Nature of Financial Statements) and its
 * prereq-first / then-filingInfo resolution now live in the shared
 * dimension-visibility foundation: the gate descriptor is registered in
 * `dimension-visibility-registry.ts` (keyed by `SHEET_KEYS.notesIssuedCapital`)
 * and resolved by `resolveDimensionVisibility()` in
 * `dimension-visibility.util.ts`. Both the pure validator
 * (`resolveActiveDimensions()`) and the mounted editor
 * (`CrossReferenceService.getVisibility()`) call that one resolver, so the
 * Consolidated/Separate gate cannot drift between the two paths.
 */

/**
 * Share-class members used as the horizontal axis of each table row. The
 * empty string encodes the "Total" column which uses the base qname without
 * a member suffix (see the component's getXbrlControlName()).
 *
 * NOTE: 'NonredeemablePreferenceSharesMember' uses lowercase 'r' to match
 * the backend's exact casing. Do not "normalise" it without a matching
 * backend change — the parity harness will throw.
 */
export const ROLE23_SHARE_CLASS_MEMBERS: ReadonlyArray<string> = [
  '', // Total (no member suffix)
  'OrdinarySharesMember',
  'RedeemablePreferenceSharesMember',
  'NonredeemablePreferenceSharesMember',
];

export type Role23Dimension = 'Consolidated' | 'Separate';
export type Role23Period = 'CurrentYear' | 'PreviousYear';

/** Narrow shape of a role 23 schema control used by the pure validator. */
export interface Role23SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: Array<{ name?: string; value?: number; min?: number }>;
  ui?: {
    control?: string;
    level?: number;
    tableColumn?: boolean;
  };
}

export interface Role23Schema {
  role: string;
  sheetId: string;
  label: string;
  controls: Role23SchemaControl[];
}

/**
 * Resolve the correct role 23 schema filename for the given accounting
 * standard. Mirrors the component's loadSchema() selection so both paths
 * derive from the same rule.
 */
export function pickRole23SchemaFile(
  accountingStandard: string | undefined | null
): string {
  if (accountingStandard === ROLE23_MPERS_ACCOUNTING_STANDARD) {
    return ROLE23_MPERS_SCHEMA_FILE;
  }
  return ROLE23_MFRS_SCHEMA_FILE;
}

/**
 * Resolve the actual role 23 schema object for the given accounting
 * standard. Used by the pure validator which cannot fetch() at validate-time
 * (the listing/dashboard's cache-miss path runs synchronously) so both
 * variants are imported eagerly and selected from memory.
 */
export function pickRole23Schema(
  accountingStandard: string | undefined | null
): Role23Schema {
  return pickSchema(
    accountingStandard,
    role23MfrsSchema as unknown as Role23Schema,
    role23MpersSchema as unknown as Role23Schema
  );
}

/**
 * Expand a single schema row's qname into the four column keys that the
 * component's getRowValidationStatus() actually inspects for a given
 * dimension (Consolidated or Separate).
 *
 * Only CurrentYear keys are returned on purpose — this mirrors the
 * component's getXbrlControlName() which hard-codes `[CurrentYear]` and
 * never checks PreviousYear cells for validation status. PreviousYear cells
 * are still built as FormControls and persisted, but the mount-based
 * validity loop ignores them. The pure validator MUST behave identically so
 * ParityCheckService does not flag false mismatches.
 *
 * (Latent bug candidate: PreviousYear required fields are effectively
 * unvalidated. Fixing it would require editing the component's status loop
 * AND the parity-check expectation; out of scope for this lift — see the
 * role lift report.)
 */
export function buildRowColumnKeys(
  qname: string,
  dimension: Role23Dimension
): string[] {
  return ROLE23_SHARE_CLASS_MEMBERS.map((member) => {
    const baseKey = member ? `${qname}:${member}` : qname;
    return `${baseKey}[${dimension}][CurrentYear]`;
  });
}

/**
 * Check if a single form-shaped value counts as "empty" for the purposes of
 * required-field validation. Matches the component's
 *   formControl.value === null || formControl.value === undefined ||
 *   formControl.value === ''
 * guard in hasColumnError() / getRowValidationStatus().
 */
export function isRole23Empty(value: unknown): boolean {
  return isEmptyCell(value);
}

/**
 * Is this schema control a validating (non-header) field?
 *
 * Mirrors buildForm()'s skip-list: SectionHeader rows never become form
 * controls and therefore cannot fail validation.
 */
export function isRole23ValidatingControl(
  control: Role23SchemaControl
): boolean {
  return control.type !== 'SectionHeader';
}

